import { useEffect, useRef, useState } from "react";

interface SectionDividerProps {
  /** Solid color above the divider (the curve fill = previous section colour). */
  topColor?: string;
  /** CSS background string for the top half when the section above uses a gradient. */
  topBackground?: string;
  /** Solid color below the divider. Use when the next section has a flat colour. */
  bottomColor?: string;
  /**
   * CSS background string for the divider's bottom half. Use when the next
   * section uses a gradient — pass the SAME background string the section
   * uses so the divider visually extends that section seamlessly.
   * Takes precedence over bottomColor when provided.
   * Example: "linear-gradient(to bottom right, hsl(194,100%,43%), hsl(194,100%,35%))"
   */
  bottomBackground?: string;
  /** Flip the curve direction */
  flip?: boolean;
  /** Draw the curve from the bottom section instead of the top section. */
  curveFromBottom?: boolean;
}

/**
 * Minimal animated divider — starts flat, gains a very subtle curve as it scrolls into view.
 * The top half is filled with topColor (the wave/curve). The bottom half inherits
 * bottomBackground (or bottomColor) so it sits flush with the next section.
 */
const SectionDivider = ({
  topColor = "#ffffff",
  topBackground,
  bottomColor = "#ffffff",
  bottomBackground,
  flip = false,
  curveFromBottom = false,
}: SectionDividerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let rafId: number | null = null;
    const update = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const raw = 1 - (rect.top + rect.height / 2) / vh;
      setProgress(Math.max(0, Math.min(1, raw)));
    };
    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const maxBow = 18;
  const bow = progress * maxBow * (flip ? -1 : 1);
  const cpY = 20 - bow * 2;
  // Top wave path — fills from y=0 down to the curve at y≈20.
  const topPath = `M0,0 L1440,0 L1440,20 Q720,${cpY} 0,20 Z`;
  const bottomPath = `M0,40 L1440,40 L1440,20 Q720,${cpY} 0,20 Z`;

  const bottomStyle: React.CSSProperties = bottomBackground
    ? { background: bottomBackground }
    : { background: bottomColor };
  const topStyle: React.CSSProperties = topBackground
    ? { background: topBackground }
    : { background: topColor };

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="relative w-full overflow-hidden"
      style={{ height: "40px", lineHeight: 0, ...(curveFromBottom ? topStyle : bottomStyle) }}
    >
      <svg
        viewBox="0 0 1440 40"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full block"
      >
        <path d={curveFromBottom ? bottomPath : topPath} fill={curveFromBottom ? bottomColor : topColor} />
      </svg>
    </div>
  );
};

export default SectionDivider;
